import { useState } from 'react';
import {
  Button, Stack, TextField, MenuItem, Typography, Alert, Divider, Box, CircularProgress, Autocomplete,
  RadioGroup, FormControlLabel, Radio, FormLabel, Table, TableHead, TableBody, TableRow, TableCell, IconButton, Paper,
} from '@mui/material';
import { UploadFile, Delete, Add, CheckCircle } from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { FileUploadField } from '../../components/FileUploadField';
import { boqUploadApi, type BoqUploadRow, type BoqUploadSummary } from '../../api/boqMasters.api';
import { clientsApi } from '../../api/clients.api';
import { lookupsApi } from '../../api/lookups.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const emptyRow = (): BoqUploadRow => ({ category: '', sow: '', description: '', unit: 'nos', quantity: null, clientRate: null, executionRate: null, taxPercent: null });

export function BoqUploadPage() {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('rate_card.create');

  const [clientId, setClientId] = useState<number | ''>('');
  const [name, setName] = useState('');
  const [geoScope, setGeoScope] = useState<'pan_india' | 'state'>('pan_india');
  const [stateIds, setStateIds] = useState<number[]>([]);
  const [fileUrl, setFileUrl] = useState('');
  const [rows, setRows] = useState<BoqUploadRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [summary, setSummary] = useState<BoqUploadSummary | null>(null);

  const clients = useQuery({ queryKey: ['clients', 'active'], queryFn: () => clientsApi.list({ limit: 200, status: 'active' }) });
  const lookups = useQuery({ queryKey: ['lookups'], queryFn: () => lookupsApi.all() });
  const states = lookups.data?.states ?? [];

  const setRow = (i: number, k: keyof BoqUploadRow, v: unknown) => setRows((arr) => arr.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  const parseMut = useMutation({
    mutationFn: () => boqUploadApi.parse(fileUrl),
    onSuccess: (r) => {
      setError(null); setSummary(null);
      if (!r.rows.length) { setInfo(r.message ?? 'No rows detected — check the file.'); setRows([]); return; }
      const cols = Object.values(r.detectedColumns).join(', ');
      setInfo(`Parsed ${r.rows.length} line item(s)${cols ? ` — mapped columns: ${cols}` : ''}. Review & edit below.`);
      setRows(r.rows.map((x) => ({ ...emptyRow(), ...x })));
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const createMut = useMutation({
    mutationFn: () => boqUploadApi.create({
      clientId: Number(clientId), name: name.trim(), geoScope,
      stateIds: geoScope === 'state' ? stateIds : undefined,
      rows: rows.filter((r) => (r.description ?? '').trim()).map((r) => ({
        ...r,
        quantity: r.quantity != null ? Number(r.quantity) : null,
        clientRate: r.clientRate != null ? Number(r.clientRate) : null,
        executionRate: r.executionRate != null ? Number(r.executionRate) : null,
        taxPercent: r.taxPercent != null ? Number(r.taxPercent) : null,
      })),
    }),
    onSuccess: (s) => { setSummary(s); setError(null); setInfo(null); },
    onError: (e) => setError(extractApiError(e)),
  });

  const validRows = rows.filter((r) => (r.description ?? '').trim());
  const canParse = Boolean(fileUrl) && !parseMut.isPending;
  const canSubmit = Boolean(clientId) && name.trim().length >= 2 && validRows.length > 0
    && (geoScope === 'pan_india' || stateIds.length > 0) && !createMut.isPending;

  const resetForNext = () => { setSummary(null); setRows([]); setFileUrl(''); setName(''); setInfo(null); };

  return (
    <>
      <PageHeader title="Upload BOQ" subtitle="Upload a client BOQ (Excel/CSV) → auto-create BOQ & SOW categories, a template, and a complete rate card scoped to Pan-India or selected states." />

      {summary ? (
        <Paper sx={{ p: 3, maxWidth: 640 }}>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center"><CheckCircle color="success" /><Typography variant="h6">BOQ imported successfully</Typography></Stack>
            <Typography variant="body2">Rate Card <strong>{summary.rateCardCode}</strong> created with <strong>{summary.itemCount}</strong> line items ({summary.geoScope === 'state' ? `${summary.stateCount} state(s)` : 'Pan-India'}).</Typography>
            <Typography variant="body2">Template <strong>{summary.templateCode}</strong> created.</Typography>
            <Typography variant="body2">New categories added — BOQ: <strong>{summary.boqCategoriesCreated}</strong>, SOW: <strong>{summary.sowCategoriesCreated}</strong>.</Typography>
            <Stack direction="row" spacing={1} mt={1}>
              <Button variant="contained" href="/rate-cards">View Rate Cards</Button>
              <Button variant="outlined" onClick={resetForNext}>Upload another BOQ</Button>
            </Stack>
          </Stack>
        </Paper>
      ) : (
        <Stack spacing={2} sx={{ maxWidth: 1100 }}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField select size="small" label="Client *" value={clientId} onChange={(e) => setClientId(e.target.value ? Number(e.target.value) : '')} sx={{ minWidth: 240 }}>
              {(clients.data?.data ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.clientName}</MenuItem>)}
            </TextField>
            <TextField size="small" label="BOQ / Rate Card name *" value={name} onChange={(e) => setName(e.target.value)} sx={{ minWidth: 280 }} helperText="e.g. Solar EPC BOQ FY26-27" />
          </Stack>

          <Box>
            <FormLabel sx={{ fontSize: 13 }}>Valid for *</FormLabel>
            <RadioGroup row value={geoScope} onChange={(e) => setGeoScope(e.target.value as 'pan_india' | 'state')}>
              <FormControlLabel value="pan_india" control={<Radio size="small" />} label="Pan India" />
              <FormControlLabel value="state" control={<Radio size="small" />} label="Specific state(s)" />
            </RadioGroup>
            {geoScope === 'state' && (
              <Autocomplete
                multiple size="small" options={states} getOptionLabel={(o) => o.name}
                value={states.filter((s) => stateIds.includes(s.id))}
                onChange={(_, val) => setStateIds(val.map((v) => v.id))}
                sx={{ maxWidth: 640 }}
                renderInput={(params) => <TextField {...params} label="States *" placeholder="Select states" />}
              />
            )}
          </Box>

          <Divider textAlign="left"><Typography variant="caption" color="text.secondary">BOQ file</Typography></Divider>
          <Box sx={{ bgcolor: 'action.hover', borderRadius: 1.5, p: 1.5 }}>
            <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
              <Box sx={{ flex: 1, minWidth: 260 }}>
                <FileUploadField label="BOQ spreadsheet (.xlsx/.xls/.csv)" accept=".xlsx,.xls,.csv" value={fileUrl} onChange={(url) => setFileUrl(url ?? '')} />
              </Box>
              <Button startIcon={parseMut.isPending ? <CircularProgress size={16} /> : <UploadFile />} variant="outlined" disabled={!canParse} onClick={() => { setInfo(null); parseMut.mutate(); }}>
                Parse file
              </Button>
            </Stack>
            {info && <Alert severity="info" sx={{ mt: 1, py: 0 }}>{info}</Alert>}
          </Box>

          {rows.length > 0 && (
            <>
              <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Line items (review & edit) — {validRows.length} item(s)</Typography></Divider>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>BOQ Category</TableCell><TableCell>SOW Category</TableCell><TableCell>Description *</TableCell><TableCell>Unit</TableCell>
                  <TableCell align="right">Qty</TableCell><TableCell align="right">Client Rate</TableCell><TableCell align="right">Exec Rate</TableCell><TableCell align="right">Tax%</TableCell><TableCell />
                </TableRow></TableHead>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell><TextField size="small" variant="standard" value={r.category ?? ''} onChange={(e) => setRow(i, 'category', e.target.value)} sx={{ width: 120 }} /></TableCell>
                      <TableCell><TextField size="small" variant="standard" value={r.sow ?? ''} onChange={(e) => setRow(i, 'sow', e.target.value)} sx={{ width: 120 }} /></TableCell>
                      <TableCell><TextField size="small" variant="standard" fullWidth value={r.description} onChange={(e) => setRow(i, 'description', e.target.value)} /></TableCell>
                      <TableCell><TextField size="small" variant="standard" value={r.unit ?? ''} onChange={(e) => setRow(i, 'unit', e.target.value)} sx={{ width: 56 }} /></TableCell>
                      <TableCell align="right"><TextField size="small" variant="standard" type="number" value={r.quantity ?? ''} onChange={(e) => setRow(i, 'quantity', e.target.value === '' ? null : Number(e.target.value))} sx={{ width: 64 }} /></TableCell>
                      <TableCell align="right"><TextField size="small" variant="standard" type="number" value={r.clientRate ?? ''} onChange={(e) => setRow(i, 'clientRate', e.target.value === '' ? null : Number(e.target.value))} sx={{ width: 80 }} /></TableCell>
                      <TableCell align="right"><TextField size="small" variant="standard" type="number" value={r.executionRate ?? ''} onChange={(e) => setRow(i, 'executionRate', e.target.value === '' ? null : Number(e.target.value))} sx={{ width: 80 }} /></TableCell>
                      <TableCell align="right"><TextField size="small" variant="standard" type="number" value={r.taxPercent ?? ''} onChange={(e) => setRow(i, 'taxPercent', e.target.value === '' ? null : Number(e.target.value))} sx={{ width: 52 }} /></TableCell>
                      <TableCell><IconButton size="small" color="error" onClick={() => setRows((arr) => arr.filter((_, j) => j !== i))}><Delete fontSize="small" /></IconButton></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Button size="small" startIcon={<Add />} onClick={() => setRows((arr) => [...arr, emptyRow()])}>Add line</Button>
                {canCreate && <Button variant="contained" disabled={!canSubmit} onClick={() => createMut.mutate()}>Create BOQ (categories + template + rate card)</Button>}
              </Stack>
            </>
          )}
        </Stack>
      )}
    </>
  );
}
